import {IUrl, IVendor as IGVLVendorInfo} from "./vendor-list";
import {Purpose} from "./purpose";
import {SpecialPurpose} from "./special-purpose";
import {Feature} from "./feature";
import {SpecialFeature} from "./special-feature";
import cmp, {site} from "../context/context";
import {DataCategory} from "./data-category";

interface IDisclosure {
    identifier: string;
    type: string;
    maxAgeSeconds?: number;
    domain?: string;
    purposes: number[];
}

class Disclosure implements IDisclosure {

    public constructor(private readonly _data: IDisclosure) {
    }

    public get identifier() : string {
        return this._data.identifier;
    }

    public get type() : string {
        return this._data.type;
    }

    public get maxAgeSeconds() : number|undefined {
        return this._data.maxAgeSeconds;
    }

    public get domain() : string|undefined {
        return this._data.domain;
    }

    public get purposes() : number[] {
        return this._data.purposes;
    }

    public relatedPurposes() : Purpose[] {
        return this.purposes.map(purposeId => Purpose.build(purposeId)).filter(purpose => purpose != null) as Purpose[];
    }

}

class Vendor {

    /* STATIC */
    public static build(vendorId:number) : Vendor|null {
        const gvlVendor: IGVLVendorInfo|null|undefined = cmp().vendorList?.getVendorInfo(vendorId);
        if (gvlVendor) {
            return new Vendor(gvlVendor);
        }
        return null;
    }

    /* INSTANCE */
    private _disclosures: Disclosure[]|undefined = undefined;

    protected constructor(protected readonly _gvlVendor:IGVLVendorInfo) {
    }

    public get id() : number {
        return this._gvlVendor.id;
    }

    public get name() : string {
        return this._gvlVendor.name;
    }

    private url(): IUrl|undefined {
        let url: IUrl|undefined = this._gvlVendor.urls?.find(url => url.langId == site.language);
        if (!url) {
            url = this._gvlVendor.urls?.find(url => url.langId == 'en');
        }
        if (!url) {
            url = this._gvlVendor.urls?.[0];
        }
        return url;
    }

    public get privacyUrl() : string {
        return this.url()?.privacy ?? '';
    }

    public get legIntClaimUrl() : string|undefined {
        return this.url()?.legIntClaim;
    }

    public get accepted() : boolean|undefined {
        return cmp().tcString?.vendors[this.id];
    }

    public get cookieMaxAgeSeconds() : number|null {
        return this._gvlVendor.cookieMaxAgeSeconds;
    }

    public get hasDeviceStorage() : boolean {
        return this._gvlVendor.deviceStorageDisclosureUrl !== undefined;
    }

    public get usesNonCookieAccess() : boolean {
        return this._gvlVendor.usesNonCookieAccess;
    }

    public get stdDataRetention(): number|undefined {
        return this._gvlVendor.dataRetention?.stdRetention;
    }

    public get purposes() : Purpose[] {
        const purposes:Purpose[] = [];
        this._gvlVendor.purposes.forEach((purposeId:number) => {
            const purpose: Purpose|null = Purpose.build(purposeId);
            if (purpose) {
                purposes.push(purpose);
            }
        });
        return purposes;
    }

    public get specialPurposes() : SpecialPurpose[] {
        const purposes:SpecialPurpose[] = [];
        this._gvlVendor.specialPurposes.forEach((purposeId:number) => {
            const purpose: SpecialPurpose|null = SpecialPurpose.build(purposeId);
            if (purpose) {
                purposes.push(purpose);
            }
        });
        return purposes;
    }

    public get features() : Feature[] {
        const features:Feature[] = [];
        this._gvlVendor.features.forEach((featureId:number) => {
            const feature: Feature|null = Feature.build(featureId);
            if (feature) {
                features.push(feature);
            }
        });
        return features;
    }

    public get specialFeatures() : SpecialFeature[] {
        const features:SpecialFeature[] = [];
        this._gvlVendor.specialFeatures.forEach((featureId:number) => {
            const feature: SpecialFeature|null = SpecialFeature.build(featureId);
            if (feature) {
                features.push(feature);
            }
        });
        return features;
    }

    public get dataCategories() : DataCategory[] {
        const dataCategories:DataCategory[] = [];
        this._gvlVendor.dataDeclaration?.forEach((dataCategoryId:number) => {
            const dataCategory: DataCategory|null = DataCategory.build(dataCategoryId);
            if (dataCategory) {
                dataCategories.push(dataCategory);
            }
        });
        return dataCategories;
    }

    public async disclosures() : Promise<Disclosure[]> {
        if (this._disclosures === undefined) {
            if (!this.hasDeviceStorage) this._disclosures = [];

            if (this._gvlVendor.deviceStorageDisclosureUrl === undefined) {
                return Promise.reject("Vendor does not have a device storage disclosure URL");
            }
            const data: IDisclosure[] = await fetch(this._gvlVendor.deviceStorageDisclosureUrl)
                .then(response => response.json())
                .then(data => data.disclosures)
            ;
            this._disclosures = data.map((disclosure: IDisclosure) => {return new Disclosure(disclosure);});
        }
        return this._disclosures;
    }

    public purposeDataRetention(purposeId: number): number|undefined {
        return this._gvlVendor.dataRetention?.purposes?.[purposeId];
    }

    public specialPurposeDataRetention(purposeId: number): number|undefined {
        return this._gvlVendor.dataRetention?.specialPurposes?.[purposeId];
    }
}

export {Vendor, Disclosure}
